import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Drawer } from '@southfields-digital/mpxlive-components';

import GraphicDetails from 'src/components/GraphicDetails';
import GraphicList from 'src/components/GraphicList/GraphicList';
import Graphics from 'src/components/Graphics';
import PlaylistSelector from 'src/components/Playlists/PlaylistSelector';
import QuickControl from 'src/components/QuickControl/QuickControl';
import RundownDialog, { DialogTypes } from 'src/components/RundownDialog';
import {
  PanelGroup,
  Panel,
  PanelResizeHandle as ResizeHandle,
} from 'src/components/ResizeablePanel';
import ViewWindow from 'src/components/ViewWindow/ViewWindow';
import { CrossListDndProvider } from 'src/components/DndKitSortableList';
import { StatusDescription } from 'src/components/GoLive';
import { getMappedCanvases } from 'src/components/GoLive/utils';
import { StateType } from 'src/redux/reducers';
import {
  REQUEST_CREATE_PLAYLIST,
  REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
  REQUEST_SET_SELECTED_PLAYLIST,
} from 'src/redux/reducers/playlist';
import { REQUEST_CREATE_PLAYLIST_GRAPHIC } from 'src/redux/reducers/playlistGraphic';

import styles from '../RundownDetail.module.scss';

function LayoutNrcsOperator() {
  const { rundownId } = useParams<{ rundownId: string }>();
  const { data, outputs } = useSelector((state: StateType) => state.rundown.detail);

  const dispatch = useDispatch();
  const { data: playlists, selectedId: activePlaylistId } = useSelector(
    (state: StateType) => state.playlist.getAll
  );
  const { selectedPlaylistGraphic } = useSelector((state: StateType) => state.playlistGraphic);

  const guaranteedActivePlaylistId = useRef<string | null>(activePlaylistId);

  const [leftPlaylistId, setLeftPlaylistId] = useState<string | null>(null);
  const [rightPlaylistId, setRightPlaylistId] = useState<string | null>(null);
  const [selectedCanvas, setSelectedCanvas] = useState<RundownTemplateCanvas | undefined>();
  const [showGraphicDrawer, setShowGraphicDrawer] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<DialogTypes | null>(null);

  // Fetch playlists for this rundown
  useEffect(() => {
    if (rundownId) {
      dispatch({
        type: REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
        payload: { rundownId, firstSelected: true },
      });
    }
  }, [dispatch, rundownId]);

  // Auto-select first playlist for columns that haven't been set yet
  useEffect(() => {
    if (!playlists?.length) return;

    if (!leftPlaylistId) {
      setLeftPlaylistId(playlists[0].id);
    }
    if (!rightPlaylistId) {
      setRightPlaylistId(playlists[0].id);
    }
  }, [playlists]);

  const handleSelectLeft = (playlistId: string) => {
    setLeftPlaylistId(playlistId);
    dispatch({ type: REQUEST_SET_SELECTED_PLAYLIST, payload: { id: playlistId } });
  };

  const handleSelectRight = (playlistId: string) => {
    setRightPlaylistId(playlistId);
    dispatch({ type: REQUEST_SET_SELECTED_PLAYLIST, payload: { id: playlistId } });
  };

  const handleGraphicSelected = (graphicId: string) => {
    dispatch({
      type: REQUEST_CREATE_PLAYLIST_GRAPHIC,
      payload: { playlistId: guaranteedActivePlaylistId.current, graphicId },
    });
    setShowGraphicDrawer(false);
  };

  const handleRundownDialog = (name: string) => {
    if (showDialog === DialogTypes.PLAYLIST) {
      dispatch({ type: REQUEST_CREATE_PLAYLIST, payload: { rundownId, name } });
    }
    setShowDialog(null);
  };

  useEffect(() => {
    guaranteedActivePlaylistId.current = activePlaylistId;
  }, [activePlaylistId]);

  useEffect(() => {
    if (data?.isLive && !selectedCanvas) {
      const activeCanvas = data.canvases.find((canvas: RundownTemplateCanvas) => canvas.output && canvas.graphics.length);
      if (activeCanvas) {
        setSelectedCanvas(activeCanvas);
      }
    }
  }, [data]);

  if (!data || !rundownId) return null;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <RundownDialog
        dialogTitle="Create Playlist"
        dialogSubtitle="Enter name for the playlist"
        isOpen={showDialog === DialogTypes.PLAYLIST}
        onClose={() => setShowDialog(null)}
        onSubmit={handleRundownDialog}
      />

      <div className="flex-1 min-h-0">
        <PanelGroup className={styles.Container} direction="horizontal">
          <CrossListDndProvider>
            {/* Left column - Primary playlist */}
            <Panel
              defaultSize={32}
              minSize={20}
              id="nrcs-playlist-left"
              order={1}
            >
              <PanelGroup direction="vertical">
                <PlaylistSelector
                  value={leftPlaylistId}
                  onChange={handleSelectLeft}
                  onAdd={() => setShowDialog(DialogTypes.PLAYLIST)}
                />

                <Panel defaultSize={100}>
                  <Graphics playlistId={leftPlaylistId} />
                </Panel>
              </PanelGroup>
            </Panel>

            <ResizeHandle />

            {/* Middle column - Secondary playlist */}
            <Panel
              defaultSize={32}
              minSize={20}
              id="nrcs-playlist-right"
              order={2}
            >
              <PanelGroup direction="vertical">
                <PlaylistSelector
                  value={rightPlaylistId}
                  onChange={handleSelectRight}
                  onAdd={() => setShowDialog(DialogTypes.PLAYLIST)}
                />

                <Panel defaultSize={100}>
                  <Graphics playlistId={rightPlaylistId} />
                </Panel>
              </PanelGroup>
            </Panel>
          </CrossListDndProvider>

          <ResizeHandle />

          {/* Right column - Preview + Details */}
          <Panel defaultSize={36} minSize={25} id="nrcs-preview" order={3}>
            <PanelGroup direction="vertical">
              <StatusDescription
                rundown={data}
                mappedCanvases={getMappedCanvases(data.canvases, outputs)}
                isLive={data.isLive}
                className="border-b-[4px] border-[#151621]"
              />

              {/* Sandbox preview */}
              <Panel className="!bg-default-surface" id="nrcs-sandbox" order={1} defaultSize={30}>
                <ViewWindow name="Sandbox" src={data.sandboxUrl} liveMode={false} />
              </Panel>

              <ResizeHandle />

              {/* PGM / Live preview */}
              {data.isLive && (
                <>
                  <Panel className="!bg-default-surface" id="nrcs-pgm" order={2} defaultSize={30}>
                    {selectedCanvas?.output?.liveUrl ? (
                      <ViewWindow name="PGM" src={selectedCanvas.output.liveUrl} liveMode />
                    ) : (
                      <ViewWindow name="PGM" src={data.sandboxUrl} liveMode />
                    )}
                  </Panel>

                  <ResizeHandle />
                </>
              )}

              {/* Graphic details / controls */}
              <Panel id="nrcs-details" order={3} defaultSize={data.isLive ? 40 : 70}>
                {selectedPlaylistGraphic ? (
                  <GraphicDetails key={rundownId} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                    Select a graphic to view details
                  </div>
                )}
              </Panel>
            </PanelGroup>
          </Panel>
        </PanelGroup>
      </div>

      {/* Bottom quick control bar — single horizontal row */}
      <div className="shrink-0 bg-default-surface px-3 py-1.5 [&>div]:flex [&>div]:flex-row [&>div]:flex-nowrap [&>div]:overflow-x-auto [&>div]:gap-2">
        <QuickControl />
      </div>

      <Drawer
        isVisible={showGraphicDrawer}
        onClose={() => setShowGraphicDrawer(false)}
        header="Graphics"
        className="overflow-auto h-full pb-4"
      >
        <GraphicList rundownId={rundownId} handleGraphicSelected={handleGraphicSelected} />
      </Drawer>
    </div>
  );
}

export default LayoutNrcsOperator;
